import { NavLink } from "react-router-dom";
import {
    LayoutDashboard, Radio, Map, FileText, Calculator, ClipboardList,
    Users, Building2, Truck, Wrench, Wallet, Receipt,
    AlertOctagon, BarChart3, Settings, Smartphone, ShieldAlert,
    Crosshair, LogOut, TrendingUp, Timer, Bell, Clock, Plug, Hospital, ListChecks, Package,
    ChevronLeft, ChevronRight, X
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNav } from "@/contexts/NavContext";
import { ROLE_LABELS } from "@/lib/constants";
import { MENU_HELP } from "@/lib/helpTexts";

// === Política de visibilidad por rol ===
// Cada link declara qué roles lo ven. Si una página no aparece aquí, NO se muestra
// — fail-safe (un rol como "ambulancia" jamás verá Dashboard).
const ADMIN = ["superadmin", "direccion"];
const FULL_OPS = [...ADMIN, "gerente", "coordinador", "cabina"];
const FINANCE = [...ADMIN, "finanzas", "comercial"];
const COMERCIAL = [...ADMIN, "comercial"];
const INTERNO_VIEW = [...FULL_OPS, "paramedico", "operador", "medico", "enfermera", "comercial", "auditor", "finanzas"];

const NAV_SECTIONS = [
    {
        label: "Operación",
        links: [
            { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard Ejecutivo", help: MENU_HELP.dashboard, roles: [...ADMIN, "gerente", "comercial", "finanzas", "auditor"] },
            { to: "/dispatch", icon: Radio, label: "Despacho Operativo", help: MENU_HELP.dispatch, roles: FULL_OPS },
            { to: "/map", icon: Map, label: "Mapa en Vivo", help: MENU_HELP.map, roles: INTERNO_VIEW },
            { to: "/services", icon: ClipboardList, label: "Servicios", help: MENU_HELP.services, roles: INTERNO_VIEW },
            { to: "/services/new", icon: FileText, label: "Nueva Solicitud", help: MENU_HELP.newService, roles: [...FULL_OPS, "comercial"] },
            { to: "/quoter", icon: Calculator, label: "Cotizador", help: MENU_HELP.quoter, roles: COMERCIAL },
            { to: "/quotes", icon: FileText, label: "Cotizaciones", help: MENU_HELP.quotes, roles: COMERCIAL },
        ],
    },
    {
        label: "Catálogos",
        links: [
            { to: "/clients", icon: Building2, label: "Clientes & Convenios", help: MENU_HELP.clients, roles: [...COMERCIAL, "cabina"] },
            { to: "/prospects", icon: Users, label: "Prospectos (CRM)", help: "Leads y cotizaciones rápidas generadas desde la calle. Conviértelos en clientes con un click.", roles: COMERCIAL },
            { to: "/agreements", icon: FileText, label: "Tarifario por Convenio", help: MENU_HELP.agreements, roles: COMERCIAL },
            { to: "/price-lists", icon: ListChecks, label: "Listas de Precios", help: "Catálogos tarifarios reutilizables.", roles: COMERCIAL },
            { to: "/profitability", icon: TrendingUp, label: "Rentabilidad", help: MENU_HELP.profitability, roles: [...ADMIN, "finanzas", "comercial"] },
            { to: "/units", icon: Truck, label: "Unidades", help: MENU_HELP.units, roles: FULL_OPS },
            { to: "/personnel", icon: Users, label: "Personal", help: MENU_HELP.personnel, roles: [...FULL_OPS, "finanzas"] },
            { to: "/attendance", icon: Clock, label: "Asistencia y Turnos", help: MENU_HELP.attendance, roles: [...FULL_OPS, "finanzas"] },
            { to: "/maintenance", icon: Wrench, label: "Mantenimiento", help: MENU_HELP.maintenance, roles: FULL_OPS },
            { to: "/inventory", icon: Package, label: "Inventario y Almacenes", help: "Catálogo + almacenes + movimientos.", roles: FULL_OPS },
        ],
    },
    {
        label: "Finanzas",
        links: [
            { to: "/receivable", icon: Receipt, label: "Cuentas por Cobrar", help: MENU_HELP.receivable, roles: FINANCE },
            { to: "/payable", icon: Wallet, label: "Cuentas por Pagar", help: MENU_HELP.payable, roles: FINANCE },
            { to: "/payroll", icon: Wallet, label: "Nómina del periodo", help: "Calcula automáticamente lo que se debe pagar a cada colaborador según su esquema (planta, hora, guardia, eventual) en el rango de fechas que selecciones.", roles: ADMIN },
            { to: "/aging", icon: BarChart3, label: "Antigüedad de Saldos", help: MENU_HELP.aging, roles: FINANCE },
        ],
    },
    {
        label: "Control",
        links: [
            { to: "/sla", icon: Timer, label: "Cumplimiento SLA", help: MENU_HELP.sla, roles: [...FULL_OPS, "auditor", "comercial"] },
            { to: "/expirations", icon: Bell, label: "Centro de alertas", help: MENU_HELP.expirations, roles: FULL_OPS },
            { to: "/incidents", icon: AlertOctagon, label: "Incidentes", help: MENU_HELP.incidents, roles: [...FULL_OPS, "auditor"] },
            { to: "/reports", icon: BarChart3, label: "Reportes", help: MENU_HELP.reports, roles: [...ADMIN, "finanzas", "comercial", "auditor"] },
            { to: "/crew", icon: Smartphone, label: "Vista Móvil Tripulación", help: MENU_HELP.crew, roles: INTERNO_VIEW },
            { to: "/audit", icon: ShieldAlert, label: "Auditoría", help: MENU_HELP.audit, roles: [...ADMIN, "auditor"] },
            { to: "/portal-users", icon: Users, label: "Usuarios Portal", help: MENU_HELP.portalUsers, roles: ADMIN },
            { to: "/users", icon: ShieldAlert, label: "Accesos del sistema", help: "Administra TODOS los logins: equipo interno, accesos de ambulancia para tablet, y clientes corporativos del portal.", roles: ADMIN },
            { to: "/hospital-contacts", icon: Hospital, label: "Contactos Hospitales", help: MENU_HELP.hospitalContacts, roles: FULL_OPS },
            { to: "/integrations", icon: Plug, label: "Integraciones", help: MENU_HELP.integrations, roles: ADMIN },
            { to: "/notifications", icon: Bell, label: "Notificaciones automáticas", help: "Configura WhatsApp/SMS/Email que el sistema envía solo cuando cambia el estatus de un servicio (plantillas editables por estatus).", roles: ADMIN },
            { to: "/settings", icon: Settings, label: "Configuración", help: MENU_HELP.settings, roles: ADMIN },
        ],
    },
];

export default function Sidebar() {
    const { user, logout } = useAuth();
    const { collapsed, setCollapsed, mobileOpen, setMobileOpen } = useNav();
    const width = collapsed ? "w-16" : "w-64";

    const NavItem = ({ link }) => {
        const Icon = link.icon;
        return (
            <NavLink
                to={link.to}
                end={link.to === "/dashboard"}
                data-testid={`nav-${link.to.replace(/\//g, "-").slice(1)}`}
                title={collapsed ? link.label : (link.help || link.label)}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                    `fm-sidebar-link ${isActive ? "active" : ""} ${collapsed ? "justify-center px-2" : ""}`
                }
            >
                <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                {!collapsed && <span className="text-[13px] truncate">{link.label}</span>}
            </NavLink>
        );
    };

    const Aside = (
        <aside
            className={`${width} bg-slate-900 text-white h-screen flex flex-col border-r border-slate-800 flex-shrink-0 transition-all duration-200`}
            data-testid="sidebar"
        >
            {/* Logo + collapse toggle */}
            <div className={`px-3 py-4 border-b border-slate-800 flex items-center gap-2 ${collapsed ? "justify-center" : "justify-between"}`}>
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 bg-red-600 flex items-center justify-center rounded-sm flex-shrink-0">
                        <Crosshair className="w-5 h-5 text-white" strokeWidth={2} />
                    </div>
                    {!collapsed && (
                        <div>
                            <div className="font-display font-black text-base leading-none tracking-tight">FindMed</div>
                            <div className="text-[9px] text-slate-400 uppercase tracking-widest mt-0.5">Dispatch Control</div>
                        </div>
                    )}
                </div>
                {/* Desktop collapse */}
                <button
                    onClick={() => setCollapsed(!collapsed)}
                    className="hidden md:flex items-center justify-center w-6 h-6 text-slate-400 hover:text-white hover:bg-slate-800 rounded-sm"
                    title={collapsed ? "Expandir menú" : "Colapsar menú"}
                    data-testid="sidebar-collapse-btn"
                >
                    {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
                </button>
                {/* Mobile close */}
                <button
                    onClick={() => setMobileOpen(false)}
                    className="md:hidden text-slate-400 hover:text-white"
                    data-testid="sidebar-mobile-close"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto py-2 fm-scroll">
                {NAV_SECTIONS.map((sec) => {
                    // Filtrar links visibles para el rol actual
                    const visible = sec.links.filter((l) => !l.roles || l.roles.includes(user?.role));
                    if (visible.length === 0) return null;
                    return (
                        <div key={sec.label} className="mb-3">
                            {!collapsed && (
                                <div className="px-5 py-1.5 text-[9px] uppercase tracking-widest font-bold text-slate-500">
                                    {sec.label}
                                </div>
                            )}
                            {collapsed && <div className="border-t border-slate-800 mx-3 my-2" />}
                            {visible.map((link) => <NavItem key={link.to} link={link} />)}
                        </div>
                    );
                })}
            </nav>

            {/* User card */}
            <div className={`border-t border-slate-800 ${collapsed ? "p-2" : "px-4 py-3"}`}>
                {!collapsed && (
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 bg-slate-700 rounded-sm flex items-center justify-center font-bold text-sm">
                            {(user?.name || "U").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold truncate">{user?.name}</div>
                            <div className="text-[10px] text-slate-400 uppercase tracking-wide">
                                {ROLE_LABELS[user?.role] || user?.role}
                            </div>
                        </div>
                    </div>
                )}
                <button
                    onClick={logout}
                    data-testid="logout-btn"
                    title="Cerrar sesión"
                    className={`w-full flex items-center justify-center gap-2 text-xs text-slate-300 hover:text-white border border-slate-700 hover:bg-slate-800 rounded-sm py-1.5 transition-colors`}
                >
                    <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {!collapsed && <span>Cerrar sesión</span>}
                </button>
            </div>
        </aside>
    );

    return (
        <>
            {/* Desktop: always visible */}
            <div className="hidden md:block">{Aside}</div>
            {/* Mobile: drawer */}
            {mobileOpen && (
                <div className="md:hidden fixed inset-0 z-50 flex" onClick={() => setMobileOpen(false)}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
                    <div onClick={(e) => e.stopPropagation()} className="relative">{Aside}</div>
                </div>
            )}
        </>
    );
}
